/** D18 天空撕裂:传播担当。左雨右晴,金光裂缝,烛民分两群;粒子在裂缝处按侧裁剪。 */
import { Graphics, Sprite } from 'pixi.js';
import { BaseEffect, EffectGroup } from './base';
import { weatherPool, eventPool } from '../engine/ParticlePool';
import { scene } from '../engine/Scene';
import { TEX } from '../engine/Tex';
import { P } from '../engine/Palette';
import { LW, LH } from '../engine/Stage';
import { rand } from '../engine/util';
import { sfx } from '../engine/Sfx';

export class SkySplitEffect extends BaseEffect {
  id = 'sky_split';
  exclusiveGroup: EffectGroup = 'sky';
  private seam = new Graphics();
  private rightTint = new Graphics();
  private leftVeil = new Graphics();
  private sun!: Sprite;
  private sunCore!: Sprite;
  private seamPts: number[] = [];
  private cx = LW * 0.5;
  private opened = 0;
  private rainAcc = 0;
  private sparkAcc = 0;

  onStart() {
    this.dur = 22; this.tail = 3;
    // 裂缝贯穿全屏(顶到画面底,而非只到岛面):撕裂的是"天",不是"到岛为止"
    let y = -20, x = this.cx;
    this.seamPts = [x, y];
    while (y < LH * 1.05) { y += rand(24, 44); x = this.cx + rand(-34, 34); this.seamPts.push(x, y); }
    // 右侧"破云之光":有核的日轮,替换旧 2.8 倍纯光晕(716px 弥散圆经 bloom
    // 一烧就是糊掉右半屏的大光斑——与主场景太阳犯的是同一个错)。
    this.sun = new Sprite(TEX.glow());
    this.sun.anchor.set(0.5); this.sun.blendMode = 'add'; this.sun.tint = P.divine; this.sun.scale.set(1.35);
    this.sun.position.set(LW * 0.8, 200);
    this.sunCore = new Sprite(TEX.core());
    this.sunCore.anchor.set(0.5); this.sunCore.blendMode = 'add'; this.sunCore.tint = 0xfff3d8; this.sunCore.scale.set(0.55);
    this.sunCore.position.copyFrom(this.sun.position);
    this.g.addChild(this.leftVeil, this.rightTint, this.sun, this.sunCore, this.seam);
    scene.crowd?.splitTwo();
    this.stage.flash.flash(0.6, 0.5);
    this.stage.shake.shake(3, 0.5);
    sfx.play('thunder');                    // 撕开
    sfx.play('bell', { delay: 0.5 });       // 神性回响
    sfx.loop('rain', { fadeIn: 3 });        // 左半世界的雨
  }

  onUpdate(dt: number) {
    const env = this.env(2);
    const gy = scene.island.surfaceYAt(this.cx);
    this.opened = Math.min(1, this.opened + dt / 0.8);
    const closing = this.t > this.dur ? Math.min(1, (this.t - this.dur) / 1.5) : 0;

    // 左半雨(裂缝处裁剪)
    this.rainAcc += (140 + 120 * this.k) * dt * env;
    while (this.rainAcc >= 1) {
      this.rainAcc -= 1;
      weatherPool?.spawn({ x: rand(0, this.cx - 8), y: -30, vx: -90, vy: 640, life: 2.4, scaleX: 0.3, scaleY: 0.24, tint: P.rain, alpha: 0.55, texture: TEX.streak(), fade: 'none' });
    }
    // 左暗右暖:撕裂的戏剧性主要来自这一步明暗对比。
    // 【修】覆盖必须超采样全屏(宽屏下世界外仍有画面,旧版 rect 只到 gy+220/LW,
    // 边界横竖硬线直接穿帮"分层感");且在裂缝两侧各用 4 级阶梯羽化,
    // 明暗从裂缝处渐变展开,而非一刀切。
    const sA = env * (1 - closing);
    const X0 = -LW * 0.35, X1 = LW * 1.35, Y0 = -LH * 0.35, H = LH * 1.8;
    const F = 90;   // 羽化带宽
    this.leftVeil.clear();
    this.leftVeil.rect(X0, Y0, this.cx - F - X0, H).fill({ color: 0x0a1026, alpha: 0.28 * sA });
    for (let i = 0; i < 4; i++) {
      this.leftVeil.rect(this.cx - F + (F / 4) * i, Y0, F / 4 + 1, H)
        .fill({ color: 0x0a1026, alpha: 0.28 * sA * (1 - (i + 0.5) / 4) });
    }
    this.rightTint.clear();
    this.rightTint.rect(this.cx + F, Y0, X1 - this.cx - F, H).fill({ color: 0xffc46b, alpha: 0.16 * sA });
    for (let i = 0; i < 4; i++) {
      this.rightTint.rect(this.cx + F - (F / 4) * (i + 1), Y0, F / 4 + 1, H)
        .fill({ color: 0xffc46b, alpha: 0.16 * sA * (1 - (i + 0.5) / 4) });
    }
    this.rightTint.blendMode = 'add';
    this.sun.alpha = 0.42 * sA;
    this.sunCore.alpha = 0.85 * sA;

    // 裂缝:三层描边(宽金晕→金柱→白芯)+ 脉动;弥合时向中收拢。
    // 旧版 11px+3px 两层里第二层 stroke 实际是空操作(v8 的 stroke() 消耗
    // path),裂缝只剩一根 11px 金线——传播担当的核心意象就这么没了。
    this.seam.clear();
    const spread = (1 - closing) * this.opened;
    const trace = () => {
      for (let i = 0; i < this.seamPts.length; i += 2) {
        const px = this.cx + (this.seamPts[i] - this.cx) * spread;
        const py = this.seamPts[i + 1] * this.opened;
        if (i === 0) this.seam.moveTo(px, py); else this.seam.lineTo(px, py);
      }
    };
    const pulse = 0.7 + 0.3 * Math.sin(this.t * 6);
    trace(); this.seam.stroke({ width: 38, color: P.divine, alpha: pulse * 0.16 * env });
    trace(); this.seam.stroke({ width: 14, color: P.divine, alpha: pulse * 0.5 * env });
    trace(); this.seam.stroke({ width: 4, color: 0xffffff, alpha: pulse * env });
    this.seam.blendMode = 'add';

    // 金色光屑沿缝升起
    this.sparkAcc += 22 * dt * env;
    while (this.sparkAcc >= 1) {
      this.sparkAcc -= 1;
      eventPool?.spawn({ x: this.cx + rand(-10, 10), y: rand(gy * 0.4, gy), vx: rand(-8, 8), vy: -rand(50, 120), life: 1.2, scale: 0.025, tint: P.divine, alpha: 0.9, blend: 'add', texture: TEX.core(), fade: 'quad' });
    }
  }

  onResolve() { this.dur = Math.min(this.dur, this.t); sfx.stop('rain', 3); }
  onDestroy() { scene.crowd?.reset(); sfx.stop('rain', 2); }
}
